import React, { useRef } from 'react'
import {
    useViewportScroll,
    motion,
    useTransform,
    useAnimation,
} from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import gsap from 'gsap'
import { ReactComponent as Arrow1 } from '../images/Arrow 1.svg'
import resume from '../images/CV_ATS_SultanSM.pdf'
import { FaHtml5, FaCss3Alt, FaJs, FaReact, FaPhp, FaLaravel, FaGitAlt, FaGithub, FaNodeJs, FaFigma, FaDocker, FaPython, } from 'react-icons/fa'
import { RiTailwindCssFill, RiBootstrapFill } from 'react-icons/ri'
import { SiMysql, SiOdoo, SiPostgresql, SiMetabase, SiLooker } from 'react-icons/si'


const transition = { duration: 1.5, ease: [.6, -.05, .01, .9] }

const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const techStack = [
    { name: 'HTML', icon: <FaHtml5 />, color: '#E34F26' },
    { name: 'CSS', icon: <FaCss3Alt />, color: '#1572B6' },
    { name: 'JS', icon: <FaJs />, color: '#F7DF1E' },
    { name: 'Tailwind', icon: <RiTailwindCssFill />, color: '#38BDF8' },
    { name: 'Bootstrap', icon: <RiBootstrapFill />, color: '#7952B3' },
    { name: 'ReactJs', icon: <FaReact />, color: '#61DAFB' },
    { name: 'PHP', icon: <FaPhp />, color: '#777BB4' },
    { name: 'Laravel', icon: <FaLaravel />, color: '#FF2D20' },
    { name: 'Figma', icon: <FaFigma />, color: '#F24E1E' },
    { name: 'Git', icon: <FaGitAlt />, color: '#F05032' },
    { name: 'GitHub', icon: <FaGithub />, color: '#ffffff' },
    { name: 'MySQL', icon: <SiMysql />, color: '#4479A1' },
    { name: 'Nodejs', icon: <FaNodeJs />, color: '#339933' },
    { name: 'Docker', icon: <FaDocker />, color: '#2496ED' },
    { name: 'Python', icon: <FaPython />, color: '#3776AB' },
    { name: 'Odoo', icon: <SiOdoo />, color: '#714B67' },
    { name: 'PostgreSQL', icon: <SiPostgresql />, color: '#4169E1' },
    { name: 'Metabase', icon: <SiMetabase />, color: '#FFFFFF' },
    { name: 'Looker Studio', icon: <SiLooker />, color: '#FFFFFF' },
]

// ---- TechCard component ----
const TechCard = ({ tech, index, inView }) => {
    const cardRef = useRef(null)
    const iconRef = useRef(null)

    React.useEffect(() => {
        const floatTween = gsap.to(iconRef.current, {
            y: -6,
            duration: 1.5 + Math.random() * 0.5,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            delay: index * 0.1
        })

        return () => floatTween.kill()
    }, [index])

    const handleMouseMove = (e) => {
        const card = cardRef.current
        const rect = card.getBoundingClientRect()
        const x = e.clientX - rect.left - rect.width / 2
        const y = e.clientY - rect.top - rect.height / 2

        gsap.to(card, {
            rotateY: x / 8,
            rotateX: -y / 8,
            transformPerspective: 500,
            duration: 0.4,
            ease: 'power2.out'
        })

        gsap.to(iconRef.current, {
            x: x / 6,
            y: y / 6,
            duration: 0.4,
            ease: 'power2.out'
        })
    }

    const handleMouseEnter = () => {
        gsap.to(cardRef.current, {
            borderColor: tech.color,
            boxShadow: `0 0 25px -5px ${hexToRgba(tech.color, 0.6)}`,
            duration: 0.4,
            ease: 'power2.out'
        })
        gsap.to(iconRef.current, {
            color: tech.color,
            scale: 1.15,
            duration: 0.4,
            ease: 'power2.out'
        })
    }

    const handleMouseLeave = () => {
        gsap.to(cardRef.current, {
            rotateY: 0,
            rotateX: 0,
            borderColor: 'rgba(255,255,255,0.15)',
            boxShadow: `0 0 25px -5px ${hexToRgba(tech.color, 0)}`,
            duration: 0.6,
            ease: 'elastic.out(1, 0.5)'
        })
        gsap.to(iconRef.current, {
            x: 0,
            y: 0,
            color: '#ffffff',
            scale: 1,
            duration: 0.6,
            ease: 'elastic.out(1, 0.5)'
        })
    }

    return (
        <motion.li
            ref={cardRef}
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ delay: index * 0.08, duration: 0.6, ease: [.6, -.05, .01, .9] }}
            className="tech-item"
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ transformStyle: 'preserve-3d', borderColor: 'rgba(255,255,255,0.15)' }}
        >
            <span className="tech-icon" ref={iconRef}>{tech.icon}</span>
            <span className="tech-name">{tech.name}</span>
        </motion.li>
    )
}
// ---- end TechCard ----

const About = ({ onCursor }) => {
    const { scrollY } = useViewportScroll();

    const y1 = useTransform(scrollY, [0, 4000], [0, -650])

    const animation = useAnimation()

    const [ref, inView] = useInView({
        triggerOnce: true
    })

    const [ref2, inView2] = useInView({
        triggerOnce: true
    })

    React.useEffect(() => {

        inView && animation.start('visible')
        inView2 && animation.start('visible2')

    }, [animation, inView, inView2])


    return (
        <>
            <section className="about maxwidth">
                <div id="about" className="about__main">
                    <motion.div className="me" alt="saltun" style={{ y: y1 }} />
                    <div className="text">
                        <h1>SULTAN</h1>
                        <h1 className="text__second">Fullstack Dev</h1>
                        <motion.div className="text__inner"
                            ref={ref}
                            animate={animation}
                            initial='hidden'
                            variants={{
                                visible: {
                                    opacity: 1,
                                    y: 0,
                                    transition: transition
                                },
                                hidden: {
                                    opacity: 0,
                                    y: 72
                                }
                            }}
                        >
                            <p>'Sup guys, my name is Sultan Syaeful Millah. I am a Fullstack Developer.
                                I am currently working at 'HR Academy Indonesia', East Jakarta, Indonesia.
                            </p>

                            <p className="interest">
                                For more about me, you can click 'Resume' button below to download my resume!
                            </p>

                            <button className="btn" onMouseLeave={onCursor} onMouseEnter={() => onCursor("hovered")}>
                                <div className="arrow">
                                    <Arrow1 className="a1" />
                                </div>
                                <a href={resume} download>Resume</a>
                            </button>
                        </motion.div>
                    </div>
                </div>

                <motion.div
                    ref={ref2}
                    animate={animation}
                    initial='hidden'
                    variants={{
                        visible2: {
                            opacity: 1,
                            y: 0,
                            transition: transition
                        },
                        hidden: {
                            opacity: 0,
                            y: 72
                        }
                    }}
                    className="tech-stack container">
                    <h3>tech - stack</h3>
                    <ul className="tech-grid">
                        {techStack.map((tech, i) => (
                            <TechCard key={tech.name} tech={tech} index={i} inView={inView2} />
                        ))}
                    </ul>
                </motion.div>
            </section>
        </>
    )
}

export default About